import React, { useEffect, useState } from "react";
import { restaurantConfig } from '../config/restaurantConfig';
import { useTranslation } from 'react-i18next';

interface ContactProps {
  language: string;
}

export function Contact({ language }: ContactProps) {
  const [isDirectionsHighlighted, setIsDirectionsHighlighted] = useState(false);
  const { t } = useTranslation();
  
  // Check if the URL has a hash that indicates the directions button should be highlighted
  useEffect(() => {
    const hash = window.location.hash;
    
    if (hash === "#directions") {
      setIsDirectionsHighlighted(true);
      
      // Scroll to the directions button
      const directionsButton = document.getElementById("directions-button");
      if (directionsButton) {
        directionsButton.scrollIntoView({ behavior: "smooth" });
      }
      
      // Reset highlight after a delay
      setTimeout(() => {
        setIsDirectionsHighlighted(false);
      }, 3000);
    }
  }, []);

  return (
    <section id="contact" className="py-16 bg-gray-50">
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem' }}>
        <div className="text-center mb-12">
          <h2 className="text-4xl lg:text-5xl font-bold mb-4 font-fleur">
            {language === 'en' ? 'Contact Us' : 'Contato'}
          </h2>
          <div className="w-24 h-1 bg-[var(--color-pink)] mx-auto mb-6"></div>
          <p className="text-gray-600 max-w-xl mx-auto">
            {language === 'en' 
              ? 'Have questions or want to make a reservation? Contact us or visit our restaurant.'
              : 'Tem perguntas ou deseja fazer uma reserva? Entre em contato conosco ou visite nosso restaurante.'}
          </p>
        </div>
        
        <div className="flex flex-col md:flex-row justify-between gap-8">
          <div className="w-full md:w-1/2 mb-10 md:mb-0 md:pr-8">
            <div className="space-y-8">
              <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'flex-start' }}>
                <div style={{ 
                  color: '#C55E86', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  width: '2.5rem', 
                  height: '2.5rem', 
                  borderRadius: '50%', 
                  backgroundColor: 'rgba(197, 94, 134, 0.1)', 
                  marginRight: '1rem' 
                }}>
                  <i className="fas fa-map-marker-alt"></i>
                </div>
                <div>
                  <span style={{ fontWeight: '500', marginBottom: '0.25rem', color: '#333333', display: 'block' }}>
                    {language === 'en' ? 'Location' : 'Localização'}
                  </span>
                  <p className="text-gray-700">{restaurantConfig.contact.address}</p>
                </div>
              </div>
              
              <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'flex-start' }}>
                <div style={{ 
                  color: '#C55E86', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  width: '2.5rem', 
                  height: '2.5rem', 
                  borderRadius: '50%', 
                  backgroundColor: 'rgba(197, 94, 134, 0.1)', 
                  marginRight: '1rem' 
                }}>
                  <i className="fas fa-directions"></i>
                </div>
                <div>
                  <span style={{ fontWeight: '500', marginBottom: '0.25rem', color: '#333333', display: 'block' }}>
                    {language === 'en' ? 'Directions' : 'Direções'}
                  </span>
                  <p>
                    <a 
                      href="https://maps.app.goo.gl/wEwj1ZNoDtqZJz3R9" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      id="directions-button"
                      className={`text-blue-600 hover:text-blue-800 ${isDirectionsHighlighted ? 'font-bold animate-pulse' : ''}`}
                    >
                      {language === 'en' ? 'Get directions' : 'Como chegar'}
                    </a>
                  </p>
                </div>
              </div>
              
              <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'flex-start' }}>
                <div style={{ 
                  color: '#C55E86', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  width: '2.5rem', 
                  height: '2.5rem', 
                  borderRadius: '50%', 
                  backgroundColor: 'rgba(197, 94, 134, 0.1)', 
                  marginRight: '1rem' 
                }}>
                  <i className="fas fa-phone"></i>
                </div>
                <div>
                  <span style={{ fontWeight: '500', marginBottom: '0.25rem', color: '#333333', display: 'block' }}>
                    {language === 'en' ? 'Phone' : 'Telefone'}
                  </span>
                  <p>
                    <a href={`tel:${restaurantConfig.phone}`} style={{ color: '#4a5568', textDecoration: 'none' }}>
                      <span className="text-lg font-medium hover:text-[#C55E86]">
                        {restaurantConfig.phone}
                      </span>
                    </a>
                  </p>
                </div>
              </div>
              

              
              <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'flex-start' }}>
                <div style={{ 
                  color: '#C55E86', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  width: '2.5rem', 
                  height: '2.5rem', 
                  borderRadius: '50%', 
                  backgroundColor: 'rgba(197, 94, 134, 0.1)', 
                  marginRight: '1rem' 
                }}>
                  <i className="fas fa-envelope"></i>
                </div>
                <div>
                  <span style={{ fontWeight: '500', marginBottom: '0.25rem', color: '#333333', display: 'block' }}>
                    {language === 'en' ? 'Email' : 'Email'}
                  </span>
                  <p>
                    <a 
                      href={`mailto:${restaurantConfig.email}`} 
                      style={{ color: '#4a5568', textDecoration: 'none' }}
                    >
                      {restaurantConfig.email}
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Opening Hours - Using direct inline HTML */}
          <div style={{ 
            maxWidth: '480px', 
            width: '100%', 
            backgroundColor: '#ffffff', 
            borderRadius: '0.75rem', 
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)', 
            padding: '2.5rem', 
            overflow: 'hidden', 
            border: '1px solid rgba(0, 0, 0, 0.05)', 
            marginLeft: 'auto', 
            marginRight: 'auto',
            position: 'relative'
          }}>
            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-[#C55E86] to-[#f8b4d9]"></div>
            
            <h3 className="text-4xl font-fleur text-center mb-4">
              {t("contactPage.workingHours")}
            </h3>
            
            <div className="w-24 h-1 bg-[var(--color-pink)] mx-auto mb-8"></div>
            
            <div className="space-y-6">
              <div className="flex items-center justify-center mb-6">
               
                
              </div>
              
              <div className="bg-gray-50 rounded-lg p-6 border-l-4 border-[#C55E86]">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-800 font-medium text-lg">
                    {t("contactPage.mondayToSunday")}
                  </span>
                  <span className="text-[#C55E86] font-bold text-xl">
                    {t("contactPage.workingHoursTime")}
                  </span>
                </div>
                <p className="text-gray-500 text-sm italic mt-2">
                  {language === 'en' ? 'Last orders taken at 21:00' : 'Últimos pedidos às 21:00'}
                </p>
              </div>
              
              <div className="text-center mt-6">
                <p className="text-gray-600 italic">
                  {language === 'en' ? 'Reservations recommended' : 'Reservas recomendadas'}
                </p>
                <a href={`tel:${restaurantConfig.phone}`} className="inline-block mt-2 text-[#C55E86] hover:text-[#a84a6e] font-medium transition-colors">
                  <i className="fas fa-phone-alt mr-2"></i>
                  {language === 'en' ? 'Book a table' : 'Reserve uma mesa'}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
} 